import type { CustomerCreateMutationVariables } from "@/v2-backoffice-client/customer/create/__types__/customer.create.mutation"
import type { CustomerMatchCandidate } from "@/v2-backoffice-client/customer/create/CustomerMatchList"
import {
  CRM_STATUS_CREATED,
  CRM_STATUS_FAILED,
  CRM_STATUS_LINKED,
  type CrmOutcome,
} from "@/v2-backoffice-client/crm/crm.constants"
import {
  MATCH_KIND_ORG_NUMBER,
  MATCH_SOURCE_BACKOFFICE,
} from "@/v2-backoffice-client/crm/crm.constants"

export type CustomerCreateInput = CustomerCreateMutationVariables["input"]

/**
 * The operator's CRM decision on the create form:
 *  - link:   a HubSpot company was picked from the matches; create + link it
 *  - create: nothing picked, "also create in HubSpot" left on (the default)
 *  - none:   nothing picked, the box unticked — Backoffice only
 */
export type CustomerCrmDecision =
  | { mode: "link"; company: CustomerMatchCandidate }
  | { mode: "create" }
  | { mode: "none" }

/** The fields a selected HubSpot company can fill in — never overwriting what the operator typed. */
const PREFILL_FIELDS = [
  "Name",
  "OrganizationNumber",
  "Phone",
  "Email",
  "Website",
  "Street",
  "PostalCode",
  "City",
  "State",
  "Country",
] as const

/**
 * Fill the form's EMPTY fields from a selected company. Typed values win:
 * the operator's intent outranks the CRM's copy, and Backoffice owns
 * identity from the moment of creation anyway (it pushes back to HubSpot).
 */
export function prefillFromCompany(
  input: CustomerCreateInput,
  company: CustomerMatchCandidate,
): CustomerCreateInput {
  const next = { ...input }
  for (const field of PREFILL_FIELDS) {
    if (next[field]) continue
    const value =
      field === "Website" ? (company.Website ?? company.Domain) : company[field]
    if (value) next[field] = value
  }
  return next
}

/** A Backoffice customer with the same org.nr is a duplicate by definition — creating another is blocked. */
export function findBlockingDuplicate(candidates: CustomerMatchCandidate[]) {
  return candidates.find(
    (candidate) =>
      candidate.Source === MATCH_SOURCE_BACKOFFICE &&
      candidate.MatchKind === MATCH_KIND_ORG_NUMBER,
  )
}

/** The submit button says exactly what will happen. */
export function getCreateButtonLabel(decision: CustomerCrmDecision) {
  switch (decision.mode) {
    case "link":
      return "Create & link to HubSpot"
    case "create":
      return "Create in Backoffice + HubSpot"
    default:
      return "Create in Backoffice only"
  }
}

/** The success toast, worded by what actually happened on the HubSpot side. */
export function getCreateOutcomeToast(
  customerName: string,
  outcome: CrmOutcome,
): { variant: "success" | "default"; title: string; description: string } {
  switch (outcome.Status) {
    case CRM_STATUS_LINKED:
      return {
        variant: "success",
        title: "Customer created and linked",
        description: `${customerName} is linked to its HubSpot company — identity fields were pushed to HubSpot.`,
      }
    case CRM_STATUS_CREATED:
      return {
        variant: "success",
        title: "Customer created in Backoffice and HubSpot",
        description: `${customerName} now exists in both, linked — sales can see it in the CRM right away.`,
      }
    case CRM_STATUS_FAILED:
      return {
        variant: "default",
        title: "Customer created — HubSpot step failed",
        description: `${customerName} exists in Backoffice. ${outcome.Message ?? "The HubSpot step did not go through."} You can link a HubSpot company from the customer panel.`,
      }
    default:
      return {
        variant: "success",
        title: "Customer created",
        description: `${customerName} exists in Backoffice only — link a HubSpot company from the customer panel whenever you want.`,
      }
  }
}
